'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePlanCheck } from '@/hooks/use-plan-check';
import type { IncomeStatement } from '@/lib/types/financials/statements';
import {
  cleanupOtherStatements,
  isDataFresh,
} from '@/lib/config/query-client-config';
import type { StatementType } from '@/lib/utils/cache-utils';

const STATEMENT_TYPE: StatementType = 'income-statement';

async function fetchIncomeStatementData(
  symbol: string,
  period: string,
  limit: number,
): Promise<IncomeStatement[]> {
  const url = `/api/fmp/income-statement?symbol=${symbol}&period=${period}&limit=${limit}`;
  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 403) {
      toast.error(
        "You've exceeded your plan's limits. Please upgrade to continue.",
      );
      throw new Error('Plan limit exceeded');
    }
    throw new Error(`Error: ${response.status} ${response.statusText}`);
  }

  const { data } = await response.json();
  if (!data || data.error) {
    throw new Error(data?.error || 'Invalid data received');
  }

  // Validate basic structure
  if (!Array.isArray(data) || !data[0]?.revenue) {
    throw new Error('Invalid income statement data structure');
  }

  // Sort data by date in descending order
  return [...data].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
}

interface UseIncomeStatementDataParams {
  symbol: string;
  period: 'annual' | 'quarter';
  years: number;
}

interface UseIncomeStatementDataReturn {
  data: IncomeStatement[];
  isLoading: boolean;
  error: Error | null;
}

export function useIncomeStatementData({
  symbol,
  period,
  years,
}: UseIncomeStatementDataParams): UseIncomeStatementDataReturn {
  const queryClient = useQueryClient();
  const { isUpdating, checkYearLimit } = usePlanCheck();

  // Calculate data points based on period
  const getDataPointsLimit = (yearsCount: number) => {
    return period === 'annual' ? yearsCount : yearsCount * 4;
  };

  const dataPoints = getDataPointsLimit(years);
  const shouldFetch = Boolean(symbol && !isUpdating && checkYearLimit(years));

  const queryKey = [STATEMENT_TYPE, symbol, period, dataPoints] as const;

  const {
    data = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => fetchIncomeStatementData(symbol, period, dataPoints),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    meta: {
      onFetch: (result: IncomeStatement[]) => {
        // Additional data validation/transformation
        if (!isDataFresh(result)) {
          queryClient.invalidateQueries({ queryKey });
        }
        // Cleanup other statement types
        cleanupOtherStatements(queryClient, STATEMENT_TYPE, symbol);
      },
    },
  });

  return {
    data,
    isLoading,
    error: error || null,
  };
}
